import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Hero: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleDomainSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanTerm = searchTerm.trim().toLowerCase();
    if (cleanTerm) {
      const termWithoutExtension = cleanTerm.replace(/\.(com|net|org|io|co|app|dev|info|biz)$/i, '');
      navigate(`/domains?search=${encodeURIComponent(termWithoutExtension)}`);
    }
  };

  return (
    <section className="bg-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column - Text Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full text-gray-700">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium">Trusted by 5,000+ websites worldwide</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
              Next-Generation 
              <span className="text-primary-navy block">Hosting <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Solutions</span></span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl">
              Experience lightning-fast performance with our cutting-edge cloud infrastructure. 
              Scale without limits, backed by enterprise-grade security.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/hosting"
                className="bg-primary-navy text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-800 transition-colors duration-200 text-center"
              >
                Start Building Today
              </Link>
              
              <Link
                to="/domains"
                className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gradient-to-r hover:from-orange-400 hover:to-yellow-500 hover:text-white hover:border-transparent transition-all duration-200 text-center"
              >
                Find Your Domain
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
              {[
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, 
                  text: '99.99% Uptime', 
                  subtext: 'SLA Guaranteed' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, 
                  text: 'Global CDN', 
                  subtext: 'Lightning Fast' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>, 
                  text: 'SSL Included', 
                  subtext: 'Always Secure' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, 
                  text: '24/7 Support', 
                  subtext: 'Expert Team' 
                }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.text}</div>
                    <div className="text-xs text-gray-500">{item.subtext}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Column - Domain Search Card */}
          <div>
            <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-lg backdrop-blur-sm relative overflow-hidden">
              {/* Glossy overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl"></div>
              <div className="relative z-10">
              {/* Card Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl mb-4 shadow-lg relative overflow-hidden">
                  {/* Icon glossy overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                  <svg className="w-8 h-8 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9-3-9m-9 9a9 9 0 919-9" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Find Your Perfect Domain</h3>
                <p className="text-gray-600">Start your digital journey with the right domain name</p>
              </div>

              {/* Search Form */}
              <form onSubmit={handleDomainSearch} className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter your domain idea..."
                    className="flex-1 px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent text-gray-900 placeholder-gray-500 text-lg"
                  />
                  <button
                    type="submit"
                    disabled={!searchTerm.trim()}
                    className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-6 py-4 rounded-xl hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                    <span className="relative z-10">Search</span>
                  </button>
                </div>
              </form>

              {/* Popular Extensions */}
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-4">Popular extensions</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  {['.com', '.net', '.org', '.io', '.co', '.app'].map((ext) => (
                    <button
                      key={ext}
                      onClick={() => setSearchTerm(prev => prev.replace(/\.[a-z]+$/i, '') + ext)}
                      className="bg-white border border-gray-300 hover:border-primary-navy text-gray-700 hover:text-primary-navy px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      {ext}
                    </button>
                  ))}
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;